// Player text filter (roadmap §7.3): posts, names, tickers, bios, group names.
// Blocks links, slurs/profanity, real-project names and real-person impersonation.
// Shared so the client can pre-check (instant feedback) and the server enforces.

import { BLOCKED_NAME_PARTS, BLOCKED_TICKERS } from "./config/blocklist";

export type TextKind = "post" | "name" | "handle" | "ticker" | "bio" | "group" | "message";

export type ModResult = { ok: true; text: string } | { ok: false; reason: string };

// Kept short and stem-based on purpose; matched after normalizing leetspeak and spacing.
const SLURS = ["nigg", "fagg", "kike", "spic", "chink", "tranny", "retard", "coon"];
const PROFANITY = ["fuck", "shit", "cunt", "bitch", "whore", "slut", "dick", "pussy", "bastard"];
const IMPERSONATE = ["elonmusk", "vitalik", "saylor", "cz_binance", "satoshi", "official", "admin", "support", "moderator", "degenworld"];

const LINK = /(https?:\/\/|www\.|\b[a-z0-9-]+\.(com|io|xyz|net|org|app|gg|co|me|ly|finance|fi|dev|ai|so|fun)\b|t\.me\/|discord\.gg)/i;

export function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/[@4]/g, "a")
    .replace(/3/g, "e")
    .replace(/[1!|]/g, "i")
    .replace(/0/g, "o")
    .replace(/[$5]/g, "s")
    .replace(/7/g, "t")
    .replace(/[^a-z]/g, "");
}

export function moderateText(raw: string, kind: TextKind, maxLen = 280): ModResult {
  const text = raw.replace(/\s+/g, " ").trim();
  if (!text) return { ok: false, reason: "Say something first." };
  if (text.length > maxLen) return { ok: false, reason: `Too long (max ${maxLen}).` };
  if (LINK.test(text)) return { ok: false, reason: "No links in player text. Keep people safe from phishing." };
  const n = normalize(text);
  if (SLURS.some((w) => n.includes(w))) return { ok: false, reason: "That word isn't allowed here." };
  if (kind !== "message" && kind !== "post" && PROFANITY.some((w) => n.includes(w))) return { ok: false, reason: "Keep names clean." };
  if (kind === "ticker" && BLOCKED_TICKERS.has(text.replace(/^\$/, "").toUpperCase())) return { ok: false, reason: "That ticker belongs to a real project." };
  if ((kind === "name" || kind === "handle" || kind === "group" || kind === "ticker") && BLOCKED_NAME_PARTS.some((p) => n.includes(normalize(p)))) return { ok: false, reason: "That name belongs to a real project." };
  if ((kind === "name" || kind === "handle" || kind === "bio" || kind === "group") && IMPERSONATE.some((p) => n.includes(p))) return { ok: false, reason: "Looks like impersonation. Pick something that's yours." };
  // Posts can swear a bit (it's CT), but stars out the worst of it.
  if (kind === "post" || kind === "message") {
    let out = text;
    for (const w of PROFANITY) out = out.replace(new RegExp(w.split("").join("[^a-z]?"), "gi"), (m) => m[0] + "*".repeat(m.length - 1));
    return { ok: true, text: out };
  }
  return { ok: true, text };
}
